import { Checkbox, Divider, Icon, Tooltip } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import type { IReduxState } from '../../../configureStore';
import type { IRequestGetOne } from '../../interfaces/requestGetOne';
import { RequestStatus } from '../../../../common/enums/requestStatus';
import { fetchJson } from '../../../store/fetch';
import { useAbortableFetch } from '../../../utils/hooks/useAbortableFetch';

interface IHideRequestedExaminationInTaskListAndHomeCheckboxProps {
    isLastItem: boolean;
    reload: () => Promise<void>;
    request: IRequestGetOne;
}

export function HideRequestedExaminationInTaskListAndHomeCheckbox(
    props: IHideRequestedExaminationInTaskListAndHomeCheckboxProps
) {
    const [
        isOrganizationUsingHideRequestedExaminationTaskFeature,
        setIsOrganizationUsingHideRequestedExaminationTaskFeature,
    ] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const organizationId = useSelector((state: IReduxState) => state.authentication.organizationId);
    const abortableFetch = useAbortableFetch();
    const { request, isLastItem, reload } = props;

    useEffect(() => {
        getServiceOrganizationSettings();
    }, [organizationId]);

    if (
        request.recipientOrganizationId !== organizationId ||
        ![RequestStatus.requested, RequestStatus.accepted].includes(request.status) ||
        !isOrganizationUsingHideRequestedExaminationTaskFeature ||
        !isLastItem
    ) {
        return null;
    }

    return (
        <>
            <Divider />
            <StyledWrapper>
                <div style={{ marginRight: '5px' }}>
                    <Checkbox
                        checked={!!request.hideRequestedExaminationInTaskListAndHome}
                        disabled={isSaving}
                        label="Afspraak ingepland"
                        onChange={async (e) => await save(e.target.checked)}
                    />
                </div>
                <Tooltip
                    content="Wanneer de afspraak is ingepland zal deze worden verborgen in de takenlijst. De taak is altijd terug te vinden door gebruik te maken van de zoekfunctie."
                    placement="bottom"
                >
                    <Icon
                        color="primary"
                        icon="information"
                    />
                </Tooltip>
            </StyledWrapper>
        </>
    );

    async function getServiceOrganizationSettings() {
        const result = await abortableFetch.fetchJson(
            `api/serviceOrganizationSettings?organizationId=${organizationId}&serviceId=${request.serviceId}&isActive=true`,
            'GET'
        );
        if (result.json.items.length) {
            setIsOrganizationUsingHideRequestedExaminationTaskFeature(
                !!result.json.items[0].isUsingHideRequestedExaminationTaskFeature
            );
        }
    }

    async function save(isChecked: boolean) {
        setIsSaving(true);
        await fetchJson(`/api/requests/${request.id}/updateHideRequestedExaminationInTaskListAndHome`, 'POST', {
            hideRequestedExaminationInTaskListAndHome: isChecked,
        });
        await reload();
        setIsSaving(false);
    }
}

const StyledWrapper = styled.div`
    margin-top: 32px;
    margin-bottom: 16px;
    justify-content: flex-end;
    display: flex;
    align-items: center;
`;
